const {KitModule, registerModule} = window;

/* eslint camelcase: "off" */

/**
 * Read a cookie value by name.
 * @param {string} name
 * @returns {string|null}
 */
function getCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolve stored UTM params from the lead cookie.
 * @returns {Record<string, string|null>}
 */
function getUtmParams() {
    const stored = getCookie('lead');
    if (stored) {
        try {
            const parsed = JSON.parse(stored);
            return {
                utm_source: parsed.UTM_Source || parsed.utm_source || null,
                utm_medium: parsed.UTM_Medium || parsed.utm_medium || null,
                utm_campaign: parsed.UTM_Campaign || parsed.utm_campaign || null,
                utm_content: parsed.UTM_Content || parsed.utm_content || null,
                utm_term: parsed.UTM_Term || parsed.utm_term || null,
            };
        } catch {
            // ignore malformed cookie
        }
    }

    return {
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_content: null,
        utm_term: null,
    };
}

/**
 * Fill HubSpot attribution fields when present on the form.
 * @param {Element|{$: Function, get: Function, jquery?: boolean}} form
 */
function setAttributionFields(form) {
    const root = form && form.jquery ? form.get(0) : form;
    if (!root || !root.querySelector) return;

    const utm = getUtmParams();
    const gclid = getCookie('googLead');

    ['utm_source', 'utm_medium', 'utm_content', 'utm_term', 'utm_campaign'].forEach(
        (name) => {
            const val = utm[name];
            if (!val) return;
            const input = root.querySelector(`input[name="${name}"]`);
            if (input) input.value = val;
        },
    );

    if (gclid) {
        const gclidInput = root.querySelector('input[name="gclid"]');
        if (gclidInput) gclidInput.value = gclid;
    }
}

/**
 * Bind demo-style submit-attempted class so errors only show after submit.
 * @param {Element|{$: Function, get: Function, jquery?: boolean}} form
 */
function bindSubmitAttempted(form) {
    const el = form && form.jquery ? form.get(0) : form;
    if (!el || el.dataset.submitErrorsBound === '1') return;
    el.dataset.submitErrorsBound = '1';

    const mark = () => {
        el.classList.add('is-submit-attempted');
    };

    el.addEventListener('submit', mark, true);
    const submitBtn = el.querySelector(
        'input[type="submit"], button[type="submit"], .hs-button',
    );
    if (submitBtn) {
        submitBtn.addEventListener('click', mark);
    }
}

/**
 * HubSpot wraps fields in unnamed <fieldset>s and injects title-less iframes.
 * Pa11y / HTMLCS require a real <legend> (not only aria-label) and iframe title.
 * @param {Element|{$: Function, get: Function, jquery?: boolean}} form
 */
function enhanceHubspotA11y(form) {
    const root = form && form.jquery ? form.get(0) : form;
    if (!root || !root.querySelectorAll) return;

    const apply = () => {
        root.querySelectorAll('fieldset').forEach((fieldset, index) => {
            if (fieldset.querySelector('legend')) return;

            const label = fieldset.querySelector(
                'label:not(.hs-error-msg):not(.hs-main-font-element)',
            );
            const name =
                (label?.textContent || '')
                    .replace(/\*/g, '')
                    .replace(/\s+/g, ' ')
                    .trim() || `Form group ${index + 1}`;

            const legend = document.createElement('legend');
            legend.className = 'is-visually-hidden';
            legend.textContent = name;
            fieldset.insertBefore(legend, fieldset.firstChild);

            if (!fieldset.getAttribute('aria-label')) {
                fieldset.setAttribute('aria-label', name);
            }
        });

        const scopes = [root, root.parentElement].filter(Boolean);
        scopes.forEach((scope) => {
            scope.querySelectorAll('iframe').forEach((iframe) => {
                if (!iframe.getAttribute('title')) {
                    iframe.setAttribute(
                        'title',
                        iframe.name?.startsWith('target_iframe') ?
                            'HubSpot form submission target' :
                            'HubSpot form helper',
                    );
                }
            });

            // Google reCAPTCHA injects a hidden unlabeled textarea.
            scope.querySelectorAll('textarea.g-recaptcha-response').forEach(
                (textarea) => {
                    if (
                        !textarea.getAttribute('aria-label') &&
                        !textarea.getAttribute('aria-labelledby') &&
                        !textarea.getAttribute('title')
                    ) {
                        textarea.setAttribute(
                            'aria-label',
                            'reCAPTCHA response',
                        );
                    }
                },
            );
        });
    };

    apply();

    // reCAPTCHA / target iframes can appear after onFormReady.
    if (root.dataset.a11yObserved === '1') return;
    root.dataset.a11yObserved = '1';

    const observer = new MutationObserver(apply);
    observer.observe(root, {childList: true, subtree: true});
    window.setTimeout(() => observer.disconnect(), 5000);
}

/**
 * Ensure HubSpot forms v2 script is available, then run callback.
 * @param {() => void} callback
 */
function ensureHubspotFormScript(callback) {
    if (window.hbspt?.forms?.create) {
        callback();
        return;
    }

    const existing = document.querySelector(
        'script[src*="js.hsforms.net/forms/v2.js"]',
    );
    if (existing || window.__hebbiaHubspotScriptLoading) {
        const poll = setInterval(() => {
            if (window.hbspt?.forms?.create) {
                clearInterval(poll);
                callback();
            }
        }, 100);
        return;
    }

    window.__hebbiaHubspotScriptLoading = true;
    const script = document.createElement('script');
    script.src = 'https://js.hsforms.net/forms/v2.js';
    script.async = true;
    script.onload = () => {
        window.__hebbiaHubspotScriptLoading = false;
        callback();
    };
    script.onerror = () => {
        window.__hebbiaHubspotScriptLoading = false;
    };
    document.body.appendChild(script);
}

/**
 * Partnership CTA — embed the partnerships HubSpot form (demo-page styling).
 */
class PartnershipCta extends KitModule {
    constructor($element) {
        super($element);
        this.formLoaded = false;
        this.loadForm();
    }

    loadForm() {
        const $target = this.$formTarget;
        if (!$target) return;

        const portalId = $target.getAttribute('data-portal-id');
        const formId = $target.getAttribute('data-form-id');
        const targetId = $target.getAttribute('data-target-id') || $target.id;

        if (!portalId || !formId || !targetId) return;

        ensureHubspotFormScript(() => {
            if (this.formLoaded || !window.hbspt?.forms?.create) return;

            window.hbspt.forms.create({
                portalId,
                formId,
                region: 'na1',
                css: '',
                cssClass: 'hubspot-form',
                target: `#${targetId}`,
                onFormReady: (form) => {
                    setAttributionFields(form);
                    bindSubmitAttempted(form);
                    enhanceHubspotA11y(form);
                },
                onFormSubmit: (form) => {
                    bindSubmitAttempted(form);
                    const el = form && form.jquery ? form.get(0) : form;
                    if (el?.classList) el.classList.add('is-submit-attempted');
                },
            });

            this.formLoaded = true;
        });
    }
}

registerModule('partnershipCta', PartnershipCta);
